import ApiService from './api.service'
import type {
  CreateApplicationDto,
  UpdateApplicationDto,
  UpdateApplicationStatusDto,
  ApplicationResponseDto,
  ApplicationDocumentResponseDto,
  StatusHistoryResponseDto,
  UpdateDocumentDto,
  PageResponseDto,
  ApplicationQueryParams,
  GroupedDocumentsDto
} from '@/types/tender'

/**
 * Service for managing tender application-related API endpoints
 * Handles all CRUD operations for applications and application documents
 */
class TenderApplicationService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Application Error'
      }
    })
  }

  /**
   * Create a new tender application
   * @param applicationData - The application data
   * @returns Promise that resolves with the created application
   */
  async createApplication(applicationData: CreateApplicationDto): Promise<ApplicationResponseDto | null> {
    return this.post('/api/tender-applications', applicationData)
  }

  /**
   * Update an existing application
   * @param id - The application ID
   * @param applicationData - The updated application data
   * @returns Promise that resolves with the updated application
   */
  async updateApplication(
    id: number,
    applicationData: UpdateApplicationDto
  ): Promise<ApplicationResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.put(`/api/tender-applications/${id}`, applicationData)
  }

  /**
   * Delete an application
   * @param id - The application ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteApplication(id: number): Promise<void | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.delete(`/api/tender-applications/${id}`)
  }

  /**
   * Get an application by ID
   * @param id - The application ID
   * @returns Promise that resolves with the application
   */
  async getApplicationById(id: number): Promise<ApplicationResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.get(`/api/tender-applications/${id}`)
  }

  /**
   * Get an application by reference number
   * @param referenceNumber - The application reference number
   * @returns Promise that resolves with the application
   */
  async getApplicationByReference(referenceNumber: string): Promise<ApplicationResponseDto | null> {
    if (!referenceNumber) {
      return Promise.reject({ message: 'No reference number provided' })
    }
    return this.get(`/api/tender-applications/reference/${referenceNumber}`)
  }

  /**
   * Get applications created by the current user (paginated)
   * @param params - Query parameters for filtering and pagination
   * @returns Promise that resolves with paginated applications
   */
  async getMyApplications(
    params?: ApplicationQueryParams
  ): Promise<PageResponseDto<ApplicationResponseDto> | null> {
    const queryString = this.buildQueryString(params)
    return this.get(`/api/tender-applications/my${queryString}`)
  }

  /**
   * Get all applications for a specific tender
   * Note: Returns only the current user's applications for this tender
   * @param tenderId - The tender ID
   * @returns Promise that resolves with list of applications
   */
  async getApplicationsByTender(
    tenderId: number
  ): Promise<ApplicationResponseDto[] | null> {
    if (!tenderId) {
      return Promise.reject({ message: 'No tender ID provided' })
    }
    return this.get(`/api/tender-applications/tender/${tenderId}`)
  }

  /**
   * Mark an application as ready for submission
   * @param id - The application ID
   * @returns Promise that resolves with the updated application
   */
  async markReadyForSubmission(id: number): Promise<ApplicationResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.post(`/api/tender-applications/${id}/ready`, {})
  }

  /**
   * Submit an application
   * @param id - The application ID
   * @returns Promise that resolves with the submitted application
   */
  async submitApplication(id: number): Promise<ApplicationResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.post(`/api/tender-applications/${id}/submit`, {})
  }

  /**
   * Update application status (for evaluators)
   * @param id - The application ID
   * @param statusData - The status update data
   * @returns Promise that resolves with the updated application
   */
  async updateApplicationStatus(
    id: number,
    statusData: UpdateApplicationStatusDto
  ): Promise<ApplicationResponseDto | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.put(`/api/tender-applications/${id}/status`, statusData)
  }

  /**
   * Get status history for an application
   * @param id - The application ID
   * @returns Promise that resolves with the status history
   */
  async getStatusHistory(id: number): Promise<StatusHistoryResponseDto[] | null> {
    if (!id) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.get(`/api/tender-applications/${id}/status-history`)
  }

  /**
   * Upload a document to an application
   * @param applicationId - The application ID
   * @param file - The document file
   * @param documentType - The type of document
   * @param description - Optional description
   * @param tags - Optional tags (comma-separated)
   * @returns Promise that resolves with the created document
   */
  async uploadDocument(
    applicationId: number,
    file: File,
    documentType: string,
    description?: string,
    tags?: string
  ): Promise<ApplicationDocumentResponseDto | null> {
    if (!applicationId || !file) {
      return Promise.reject({ message: 'Application ID and file are required' })
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)
    if (description) formData.append('description', description)
    if (tags) formData.append('tags', tags)

    return this.post(`/api/tender-applications/${applicationId}/documents`, formData)
  }

  /**
   * Upload multiple documents to an application in batch
   * @param applicationId - The application ID
   * @param files - Array of document files
   * @param documentType - The type of documents (same for all)
   * @param description - Optional description (same for all)
   * @param tags - Optional tags (comma-separated, same for all)
   * @returns Promise that resolves with array of created documents
   */
  async uploadDocumentsBatch(
    applicationId: number,
    files: File[],
    documentType: string,
    description?: string,
    tags?: string
  ): Promise<ApplicationDocumentResponseDto[] | null> {
    if (!applicationId || !files || files.length === 0) {
      return Promise.reject({ message: 'Application ID and at least one file are required' })
    }

    const formData = new FormData()
    
    // Append all files
    files.forEach(file => {
      formData.append('files', file)
    })
    
    formData.append('documentType', documentType)
    if (description) formData.append('description', description)
    if (tags) formData.append('tags', tags)

    return this.post(`/api/tender-applications/${applicationId}/documents/batch`, formData)
  }

  /**
   * Download an application document
   * @param documentId - The document ID
   * @returns Promise that resolves with the file blob
   */
  async downloadDocument(documentId: number): Promise<Blob | null> {
    if (!documentId) {
      return Promise.reject({ message: 'No document ID provided' })
    }
    return this.get(`/api/tender-applications/documents/${documentId}`, {
      responseType: 'blob'
    })
  }

  /**
   * Delete an application document
   * @param documentId - The document ID
   * @returns Promise that resolves when deletion is complete
   */
  async deleteDocument(documentId: number): Promise<void | null> {
    if (!documentId) {
      return Promise.reject({ message: 'No document ID provided' })
    }
    return this.delete(`/api/tender-applications/documents/${documentId}`)
  }

  /**
   * Update application document metadata
   * @param documentId - The document ID
   * @param updateData - The updated metadata
   * @returns Promise that resolves with the updated document
   */
  async updateDocument(
    documentId: number,
    updateData: UpdateDocumentDto
  ): Promise<ApplicationDocumentResponseDto | null> {
    if (!documentId) {
      return Promise.reject({ message: 'No document ID provided' })
    }
    return this.put(`/api/tender-applications/documents/${documentId}`, updateData)
  }

  /**
   * Get all documents for an application
   * @param applicationId - The application ID
   * @returns Promise that resolves with the list of documents
   */
  async getApplicationDocuments(applicationId: number): Promise<ApplicationDocumentResponseDto[] | null> {
    if (!applicationId) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.get(`/api/tender-applications/${applicationId}/documents`)
  }

  /**
   * Get documents for an application grouped by type
   * @param applicationId - The application ID
   * @returns Promise that resolves with documents grouped by type
   */
  async getGroupedDocuments(applicationId: number): Promise<GroupedDocumentsDto | null> {
    if (!applicationId) {
      return Promise.reject({ message: 'No application ID provided' })
    }
    return this.get(`/api/tender-applications/${applicationId}/documents/grouped`)
  }

  /**
   * Build query string from parameters
   * @param params - Query parameters
   * @returns Query string with leading ?
   */
  private buildQueryString(params?: ApplicationQueryParams): string {
    if (!params) return ''
    
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    
    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

// Create a singleton instance
const tenderApplicationService = new TenderApplicationService()

// Export the singleton
export default tenderApplicationService
